import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import {
  Product,
  ProductListResponse,
  ProductCreate,
  ProductUpdate,
} from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.tsx';

const PAGE_SIZE = 10;

interface UseProductResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchProducts: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createProduct: (data: ProductCreate) => Promise<number | boolean>; // Modified return type
  updateProduct: (id: number, data: ProductUpdate) => Promise<boolean>;
  deleteProduct: (id: number) => Promise<boolean>;
}

export const useProduct = (): UseProductResult => {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<ProductListResponse>(
        `/products?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setProducts(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch Products: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching Products.');
      }
      setProducts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchProducts(currentPage);
  }, [currentPage, fetchProducts]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createProduct = async (data: ProductCreate): Promise<number | boolean> => { // Modified return type
    setError(null);
    try {
      const newProduct = await apiClient<Product>('/products', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchProducts(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return newProduct.id!; // Return the ID of the newly created product
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create Product: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateProduct = async (id: number, data: ProductUpdate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Product>(`/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchProducts(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update Product: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteProduct = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Product ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/products/${id}`, { method: 'DELETE' });
      const pageToFetch = products.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchProducts(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete Product: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    products,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchProducts,
    handlePageChange,
    createProduct,
    updateProduct,
    deleteProduct,
  };
};